import { useEffect, useState, type ReactNode } from 'react';
import { areCookiesEnabled, setConsent } from '@/lib/consent';
import { loadGA4IfConfigured, loadSkimlinksIfConfigured } from '@/lib/load-scripts';
import { initSentryIfConfigured } from '@/lib/sentry';
import CookieWall from './CookieWall';

interface ConsentProviderProps {
  children: ReactNode;
}

export default function ConsentProvider({ children }: ConsentProviderProps) {
  // Cookie detection has to be synchronous so we can render the cookie wall
  // on the first paint when cookies are blocked. It does not inject any
  // scripts, so it is cheap.
  const [cookiesBlocked] = useState(() => !areCookiesEnabled());

  useEffect(() => {
    if (cookiesBlocked) return;
    setConsent();

    // Sentry collects errors, traces, and (sampled) session replays — all
    // gated on consent the same way GA4/Skimlinks are.
    initSentryIfConfigured();

    // Skimlinks must rewrite merchant <a> tags before users click them; deferring
    // to idle opens a window where fast clicks miss affiliate attribution.
    loadSkimlinksIfConfigured();

    // GA4 isn't in the click path. Deferring to idle keeps ~50KiB out of the
    // critical path on slow networks.
    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
    }).requestIdleCallback;
    if (typeof idle === 'function') {
      idle(loadGA4IfConfigured, { timeout: 2000 });
    } else {
      // Safari <16 has no requestIdleCallback.
      setTimeout(loadGA4IfConfigured, 0);
    }
  }, [cookiesBlocked]);

  return (
    <>
      {children}
      {cookiesBlocked && <CookieWall />}
    </>
  );
}
